import type { Action, Competition, GroupsConfig, Id, Match, MatchResult, Phase, Tournament } from '../../types.js';
import { validateResult, walkoverResult } from '../../rules/validate.js';
import { propagate, unpropagate } from '../../bracket/single.js';
import { updateMeasuredMinutes } from '../../eta.js';
import { EngineError } from '../state.js';
import { clone } from '../../clone.js';
import { involves, matchOf, rulesOf, teamOf } from './shared.js';
import { slotTeamIds } from '../../slots.js';

// ---------------------------------------------------------------------------
// Spiele
// ---------------------------------------------------------------------------

export function applyMatch(t: Tournament, action: Action): string[] {
  const warnings: string[] = [];
  switch (action.type) {
    case 'match.start':
      startMatch(t, action.matchId, action.courtId, action.at);
      break;
    case 'match.score': {
      // Zwischenstand (docs/notes/livescore.md): nur für laufende Spiele, kein Ergebnis
      const { match } = matchOf(t, action.matchId);
      if (match.status !== 'running') throw new EngineError('Spiel läuft nicht — Zwischenstand nur für laufende Spiele');
      for (const v of [action.a, action.b]) {
        if (!Number.isInteger(v) || v < 0 || v > 99) throw new EngineError('Zwischenstand muss zwischen 0 und 99 liegen');
      }
      match.live = { a: action.a, b: action.b, at: action.at };
      break;
    }
    case 'match.result':
      warnings.push(...enterResult(t, action.matchId, action.result, action.force ?? false, action.at));
      break;
    case 'match.walkover': {
      const { comp, phase, match } = matchOf(t, action.matchId);
      if (match.a.kind !== 'team' || match.b.kind !== 'team') throw new EngineError('Teilnehmer stehen noch nicht fest');
      if (match.status === 'finished' || match.status === 'walkover') throw new EngineError('Spiel ist schon beendet');
      finishMatch(t, comp, phase, match, walkoverResult(rulesOf(comp, phase), action.winner), 'walkover', action.at);
      break;
    }
    case 'match.reopen': {
      const { phase, match } = matchOf(t, action.matchId);
      if (match.status !== 'finished' && match.status !== 'walkover') throw new EngineError('Spiel ist nicht beendet');
      if (phase.status === 'finished') throw new EngineError('Phase ist abgeschlossen');
      try {
        unpropagate(phase.matches, match);
      } catch (e) {
        throw new EngineError((e as Error).message);
      }
      delete match.result;
      delete match.endedAt;
      delete match.startedAt;
      delete match.courtId;
      delete match.annulled;
      match.status = 'ready';
      break;
    }
    case 'match.abort': {
      // Laufendes Spiel abbrechen: Feld frei, zurück in die Warteschlange
      const { match } = matchOf(t, action.matchId);
      if (match.status !== 'running') throw new EngineError('Spiel läuft nicht');
      freeCourt(t, match);
      delete match.live;
      delete match.startedAt;
      delete match.courtId;
      match.status = 'ready';
      break;
    }
    case 'match.setPriority':
      matchOf(t, action.matchId).match.priority = action.priority;
      break;
    case 'match.order': {
      const ids = action.matchIds;
      if (ids.length === 0 || ids.length > 1000 || ids.some((id) => typeof id !== 'string')) throw new EngineError('Reihenfolge ungültig');
      if (new Set(ids).size !== ids.length) throw new EngineError('Spiel doppelt in der Reihenfolge');
      const genannt = ids.map((id) => matchOf(t, id).match);
      // Über alle anderen wartenden Spiele: die genannten bekommen höhere Prioritäten, absteigend in ihrer Reihenfolge
      const rest = new Set(ids);
      let basis = 0;
      for (const c of t.competitions) for (const p of c.phases) for (const m of p.matches) if (!rest.has(m.id) && (m.status === 'ready' || m.status === 'open')) basis = Math.max(basis, m.priority);
      genannt.forEach((m, i) => (m.priority = basis + genannt.length - i));
      break;
    }
    case 'match.cancel': {
      const { match } = matchOf(t, action.matchId);
      if (match.status === 'finished' || match.status === 'walkover') throw new EngineError('Beendete Spiele können nicht abgesagt werden');
      freeCourt(t, match);
      match.status = 'cancelled';
      break;
    }
  }
  return warnings;
}

/** Platz eines Spiels: KO-Runde bzw. Gruppe vor Phase (docs/17, docs/notes/ko-runden.md) */
export function requiredCourtGroup(phase: Phase, match: Match): string | undefined {
  const round = phase.koRounds?.find((r) => r.matchIds.includes(match.id));
  if (round) return round.courtGroup ?? phase.config.courtGroup;
  const g = match.groupId ? phase.groups.find((x) => x.id === match.groupId) : undefined;
  return g?.courtGroup ?? phase.config.courtGroup;
}

function startMatch(t: Tournament, matchId: Id, courtId: Id, at: number): void {
  const { comp, phase, match } = matchOf(t, matchId);
  if (match.status !== 'ready') {
    const label: Record<Match['status'], string> = {
      open: 'noch ohne Teilnehmer',
      ready: 'bereit',
      running: 'schon gestartet',
      finished: 'schon beendet',
      walkover: 'als Walkover gewertet',
      cancelled: 'abgesagt',
    };
    throw new EngineError(`Spiel ist ${label[match.status]} — nicht startbar`);
  }
  const court = t.courts.find((c) => c.id === courtId);
  if (!court) throw new EngineError('Feld nicht gefunden');
  if (!court.active) throw new EngineError(`Feld ${court.name} ist inaktiv`);
  if (court.currentMatchId) throw new EngineError(`Feld ${court.name} ist belegt`);
  const group = requiredCourtGroup(phase, match);
  if (group && court.group !== group) throw new EngineError(`Spiel gehört auf Platz „${group}" — ${court.name} liegt woanders`);
  if (match.a.kind !== 'team' || match.b.kind !== 'team') throw new EngineError('Teilnehmer stehen noch nicht fest');
  for (const id of [...slotTeamIds(match.a), ...slotTeamIds(match.b)]) {
    const team = teamOf(comp, id);
    if (team.status === 'withdrawn' || team.status === 'disqualified') throw new EngineError(`${team.name} ist zurückgezogen`);
    const busy = comp.phases.some((p) => p.matches.some((m) => m.status === 'running' && involves(m, id)));
    if (busy) throw new EngineError(`${team.name} spielt gerade`);
  }
  match.status = 'running';
  match.courtId = courtId;
  match.startedAt = at;
  court.currentMatchId = match.id;
  if (phase.status === 'drawn') phase.status = 'running';
  if (comp.status === 'setup') comp.status = 'running';
  if (t.status === 'draft') t.status = 'running';
}

function enterResult(t: Tournament, matchId: Id, result: MatchResult, force: boolean, at: number): string[] {
  const { comp, phase, match } = matchOf(t, matchId);
  if (match.a.kind !== 'team' || match.b.kind !== 'team') throw new EngineError('Teilnehmer stehen noch nicht fest');
  if (match.status === 'cancelled') throw new EngineError('Spiel ist abgesagt');
  if (phase.status === 'finished') throw new EngineError('Phase ist abgeschlossen');
  const rules = rulesOf(comp, phase);
  const drawsAllowed = rules.draws === 'groupOnly' && phase.type === 'groups';
  const v = validateResult(rules, result, { drawsAllowed });
  if (!v.ok) throw new EngineError(v.errors.join('; '), v.warnings);
  if (v.warnings.length > 0 && !force) throw new EngineError(`Ergebnis unplausibel: ${v.warnings.join('; ')} (mit force übersteuerbar)`, v.warnings);
  if (match.status === 'finished' || match.status === 'walkover') {
    // Korrektur: erst Abhängigkeiten lösen
    try {
      unpropagate(phase.matches, match);
    } catch (e) {
      throw new EngineError((e as Error).message);
    }
  }
  if (match.status === 'ready') {
    // Ergebnis ohne vorheriges „Starten" (Kickertool erlaubt das auch)
    if (phase.status === 'drawn') phase.status = 'running';
    if (comp.status === 'setup') comp.status = 'running';
    if (t.status === 'draft') t.status = 'running';
  }
  finishMatch(t, comp, phase, match, clone(result), 'finished', at);
  return v.warnings;
}

export function finishMatch(t: Tournament, comp: Competition, phase: Phase, match: Match, result: MatchResult, status: 'finished' | 'walkover', at: number): void {
  const wasRunning = match.status === 'running';
  match.result = result;
  match.status = status;
  match.endedAt = at;
  delete match.annulled;
  delete match.live;
  freeCourt(t, match);
  if (wasRunning && match.startedAt !== undefined && status === 'finished') {
    const rules = rulesOf(comp, phase);
    comp.measuredMatchMinutes = updateMeasuredMinutes(comp.measuredMatchMinutes, rules.plannedMatchMinutes, (at - match.startedAt) / 60_000);
  }
  propagate(phase.matches, match);
  autoWalkovers(t, comp, phase, at);
}

function freeCourt(t: Tournament, match: Match): void {
  if (!match.courtId) return;
  const court = t.courts.find((c) => c.id === match.courtId);
  if (court && court.currentMatchId === match.id) delete court.currentMatchId;
}

/** Spiele, in denen ein zurückgezogenes Team steht, automatisch als Walkover werten */
export function autoWalkovers(t: Tournament, comp: Competition, phase: Phase, at: number): void {
  const rules = rulesOf(comp, phase);
  const gone = new Set(comp.teams.filter((x) => x.status === 'withdrawn' || x.status === 'disqualified').map((x) => x.id));
  if (phase.type === 'groups' && (phase.config as GroupsConfig).partners) {
    // Wechselnde Partner: kein Walkover — er träfe den Partner des Abgemeldeten (0:21) und schenkte den Gegnern einen Sieg
    for (const m of phase.matches) {
      if (m.status !== 'ready' && m.status !== 'open') continue;
      const weg = [...slotTeamIds(m.a), ...slotTeamIds(m.b)].find((id) => gone.has(id));
      if (!weg) continue;
      m.status = 'cancelled';
      m.cancelledFor = weg;
    }
    return;
  }
  let changed = true;
  while (changed) {
    changed = false;
    for (const m of phase.matches) {
      if (m.status !== 'ready' || m.a.kind !== 'team' || m.b.kind !== 'team') continue;
      const aGone = slotTeamIds(m.a).some((id) => gone.has(id));
      const bGone = slotTeamIds(m.b).some((id) => gone.has(id));
      if (!aGone && !bGone) continue;
      if (aGone && bGone) {
        // beide weg: Sieger nach Setzreihenfolge egal — A gewinnt, protokolliert als Walkover
        finishMatch(t, comp, phase, m, walkoverResult(rules, 'A'), 'walkover', at);
      } else finishMatch(t, comp, phase, m, walkoverResult(rules, aGone ? 'B' : 'A'), 'walkover', at);
      changed = true;
    }
  }
}
